import { useEffect, useRef } from "react";

/**
 * Test mode's idle limit (contract 0.10.58, db/README Revision 100). The server ends test mode after `idleMinutes` without a
 * write; reads never count, so a page that only polls cannot keep it alive. While the admin reads, the SPA proves they are
 * still there: after the person has used the page (pointer, key, wheel, scroll, touch) it sends the heartbeat
 * (`POST /auth/test-mode/activity`) at most once per interval. Once the limit could have passed with no heartbeat, it asks
 * the server again (`recheck`), which ends test mode and answers the real identity; so does a heartbeat answered "not active".
 */

/** The longest gap between two heartbeats. */
export const MAX_HEARTBEAT_MS = 60_000;

/** Margin after the limit before the SPA asks the server again. */
export const RECHECK_MARGIN_MS = 2_000;

const USE_EVENTS = ["pointerdown", "keydown", "wheel", "scroll", "touchstart"] as const;

/** A heartbeat interval well inside the limit: a third of it, at most a minute, at least ten seconds. */
export function heartbeatIntervalMs(idleMinutes: number): number {
  return Math.max(10_000, Math.min(MAX_HEARTBEAT_MS, Math.floor((idleMinutes * 60_000) / 3)));
}

export interface TestModeHeartbeatOptions {
  /** Test mode is on. */
  active: boolean;
  /** The server's idle limit; nothing runs without one. */
  idleMinutes: number | null | undefined;
  /** Sends the heartbeat; resolves whether test mode is still active. A rejection counts as "ask again". */
  send: () => Promise<boolean>;
  /** Refetches the session (and everything computed for the test identity). */
  recheck: () => void;
}

export function useTestModeHeartbeat({ active, idleMinutes, send, recheck }: TestModeHeartbeatOptions): void {
  const sendRef = useRef(send);
  const recheckRef = useRef(recheck);
  useEffect(() => {
    sendRef.current = send;
    recheckRef.current = recheck;
  });

  useEffect(() => {
    if (!active || !idleMinutes || idleMinutes <= 0) return undefined;
    let used = false;
    let stopped = false;
    let idleTimer: number | undefined;
    const armIdle = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => recheckRef.current(), idleMinutes * 60_000 + RECHECK_MARGIN_MS);
    };
    const markUsed = () => {
      used = true;
    };
    const beat = window.setInterval(() => {
      if (!used) return;
      used = false;
      sendRef.current().then(
        (stillActive) => {
          if (stopped) return;
          if (stillActive) armIdle();
          else recheckRef.current();
        },
        () => {
          if (!stopped) recheckRef.current();
        },
      );
    }, heartbeatIntervalMs(idleMinutes));
    for (const type of USE_EVENTS) document.addEventListener(type, markUsed, { capture: true, passive: true });
    armIdle();
    return () => {
      stopped = true;
      window.clearInterval(beat);
      window.clearTimeout(idleTimer);
      for (const type of USE_EVENTS) document.removeEventListener(type, markUsed, { capture: true });
    };
  }, [active, idleMinutes]);
}
